import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/content";
import { scrollTo } from "./Reveal";
import { ArrowUpRight } from "lucide-react";

const lineV = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.15 + i * 0.12, ease: [0.76, 0, 0.24, 1] } }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span custom={i} variants={lineV} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen pt-[68px] overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-10 md:pt-16 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary mb-6"
          >
            <span className="w-8 h-px bg-primary" /> {h.badge}
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter text-[13vw] sm:text-[10vw] lg:text-[6.6vw] leading-[0.92]">
            <Line i={0}>{h.lineTop}</Line>
            <Line i={1}>
              {h.lineMidA}
              <span className="font-serif-ed italic font-medium text-primary">{h.lineMidEm}</span>
              {h.lineMidB}
            </Line>
            <Line i={2}>{h.lineBottom}</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-6 font-serif-ed italic text-2xl md:text-3xl text-primary leading-tight"
          >
            {h.slogan}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-5 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed"
          >
            {h.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-join"
              onClick={() => scrollTo("#join")}
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full pl-6 pr-2 py-2.5 hover:bg-[hsl(214,76%,24%)] active:scale-95 transition-all"
            >
              {h.ctaPrimary}
              <span className="grid place-items-center w-8 h-8 rounded-full bg-accent text-accent-foreground group-hover:rotate-45 transition-transform">
                <ArrowUpRight size={16} />
              </span>
            </button>
            <button
              data-testid="hero-cta-sportello"
              onClick={() => scrollTo("#sportello")}
              className="inline-flex items-center gap-2 font-semibold rounded-full px-6 py-3 border border-foreground/20 hover:bg-foreground hover:text-background transition-colors"
            >
              {h.ctaSecondary}
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1 }}
            className="relative rounded-3xl overflow-hidden aspect-[3/4] border border-black/5"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Genitore e bambino"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
            <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur-md rounded-2xl p-4">
              <p className="font-serif-ed italic text-xl leading-tight">"Nessun genitore si senta solo."</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
